import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Button, Checkbox, Input, message, Modal, Radio } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { endCallApi } from "../../redux/api/Call/EndCallSlice";
import { getToken } from "../../redux/api/Token/GetTokenSlice";
import { 
  uploadFileApi, 
  consultationObservationApi, 
  clearUploadedFiles 
} from "../../redux/api/Call/ConsultationObservationSlice";
import { ToastContainer, toast } from "react-toastify";
import {
  LocalUser,
  RemoteUser,
  useIsConnected,
  useJoin,
  useLocalMicrophoneTrack,
  useLocalCameraTrack,
  usePublish,
  useRemoteUsers,
  useRTCClient,
} from "agora-rtc-react";
import "./styles.css";
import { LuMic } from "react-icons/lu";
import { navigateTo, getPathSegment } from "../../utils/navigation";
import { FiMicOff } from "react-icons/fi";
import { getAppointment } from "../../redux/action/Appointment";
import GoBackModel from "../../Component/GoBackModel";
import PrescriptionWarningModal from "../../Component/PrescriptionWarningModal";
import RiskRatingModal from "../../Component/RiskRatingModal";
import NotesAndRecommendations from "../../Component/NotesAndRecommendations";
import SharedDocuments from "../../Component/SharedDocuments";

export default function Index({ placeholder }) {
  const dispatch = useDispatch();
  const { data, loading } = useSelector((state) => state.GET_APPOINTMENT);
  const { 
    loading: consultationLoading, 
    uploadLoading, 
    uploadedFiles 
  } = useSelector((state) => state.CONSULTATION_OBSERVATION);

  const [shouldJoin, setShouldJoin] = useState(false);
  const [mode, setMode] = useState(getPathSegment(3));
  const [id, setId] = useState(getPathSegment(4));
  const [uid, setUid] = useState(getPathSegment(5));
  const localToken = localStorage.getItem("vToken");
  const [risk, setRisk] = useState(null);
  const [calling, setCalling] = useState(true);
  const isConnected = useIsConnected();
  const [appId, setAppId] = useState("7338cf3deca7453f8ef37d8e7fdf5946");
  const [channel, setChannel] = useState(
    getPathSegment(2)
  );
  const [signalLevel, setSignalLevel] = useState(0);
  const [goBackModel, setGoBackModel] = useState(false);
  const [token, setToken] = useState(null);
  const [prescriptionNotRequired, setPrescriptionNotRequired] = useState(false);
  const [notes, setNotes] = useState(null);
  const [prescription, setPrescription] = useState(null);
  
  // File states for recommendations
  const [recommendationFiles, setRecommendationFiles] = useState([]);
  
  // New observation states
  const [selfHarmSymptoms, setSelfHarmSymptoms] = useState("LOW");
  const [depressionSymptoms, setDepressionSymptoms] = useState("LOW");
  const [anxietySymptoms, setAnxietySymptoms] = useState("LOW");
  const [angerIssues, setAngerIssues] = useState("LOW");
  
  // New recommendation states
  const [assessments, setAssessments] = useState("");
  const [exercises, setExercises] = useState("");
  const [clinicalConsultation, setClinicalConsultation] = useState(false);
  const [medicalConsultation, setMedicalConsultation] = useState(false);
  
  // File upload states
  const [assessmentFiles, setAssessmentFiles] = useState([]);
  const [exerciseFiles, setExerciseFiles] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [value, setValue] = useState(null);
  const [leftCall, setLeftCall] = useState(false);
  const [isTokenFetched, setIsTokenFetched] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [micOn, setMicOn] = useState(true);
  const [cameraOn, setCameraOn] = useState(mode === "VIDEO");
  const [prescriptionAlert, setPrescriptionAlert] = useState(false);
  const [forceEndWithoutPrescription, setForceEndWithoutPrescription] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [remoteMuted, setRemoteMuted] = useState(false);
  const [connectionLost, setConnectionLost] = useState(false);
  const [rejoining, setRejoining] = useState(false);
  const draftKey = `callDraft:${id}`;
  const riskOptions = [
    { label: "High Risk", value: "HIGH_RISK", color: "red" },
    { label: "Moderate Risk", value: "MODERATE_RISK", color: "orange" },
    { label: "Low Risk", value: "LOW_RISK", color: "goldenrod" },
    { label: "No Risk", value: "NO_RISK", color: "green" },
  ];

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const getRiskColor = (riskValue) => {
    const option = riskOptions.find(opt => opt.value === riskValue);
    return option ? option.color : "#FFD700";
  };

  // Handle mobile video overlay when modals are open
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      if (isModalOpen || prescriptionAlert) {
        document.body.classList.add('modal-open-mobile');
      } else {
        document.body.classList.remove('modal-open-mobile');
      }
    }
    
    // Cleanup on component unmount
    return () => {
      document.body.classList.remove('modal-open-mobile');
    };
  }, [isModalOpen, prescriptionAlert]);

  const generateToken = () => {
    dispatch(getToken(id)).then((res) => {
      setToken(res.payload.message.payload);
      setIsTokenFetched(true);
      setCalling(true);
    });
  };

  // Backend issues a fresh token on every request, so renewals and rejoins never reuse an expired one.
  const fetchFreshToken = async () => {
    const res = await dispatch(getToken(id));
    return res?.payload?.message?.payload;
  };

  // Restore an unsaved end-of-call form after a refresh or rejoin (attached files can't be kept).
  useEffect(() => {
    try {
      const draft = JSON.parse(localStorage.getItem(draftKey) || "null");
      if (!draft) return;
      setValue(draft.value ?? null);
      setNotes(draft.notes ?? null);
      setPrescription(draft.prescription ?? null);
      setPrescriptionNotRequired(!!draft.prescriptionNotRequired);
      setSelfHarmSymptoms(draft.selfHarmSymptoms || "LOW");
      setDepressionSymptoms(draft.depressionSymptoms || "LOW");
      setAnxietySymptoms(draft.anxietySymptoms || "LOW");
      setAngerIssues(draft.angerIssues || "LOW");
      setAssessments(draft.assessments || "");
      setExercises(draft.exercises || "");
      setClinicalConsultation(!!draft.clinicalConsultation);
      setMedicalConsultation(!!draft.medicalConsultation);
    } catch (e) {
      console.error("Failed to restore call draft", e);
    }
  }, [draftKey]);

  useEffect(() => {
    try {
      localStorage.setItem(draftKey, JSON.stringify({
        value, notes, prescription, prescriptionNotRequired,
        selfHarmSymptoms, depressionSymptoms, anxietySymptoms, angerIssues,
        assessments, exercises, clinicalConsultation, medicalConsultation,
      }));
    } catch (e) {
      console.error("Failed to save call draft", e);
    }
  }, [draftKey, value, notes, prescription, prescriptionNotRequired, selfHarmSymptoms, depressionSymptoms,
      anxietySymptoms, angerIssues, assessments, exercises, clinicalConsultation, medicalConsultation]);

  useEffect(() => {
    const interval = setInterval(() => {
      setSeconds((prev) => prev + 1);
    }, 1000);

    // Clean up the interval when component unmounts
    return () => clearInterval(interval);
  }, []);

  const formatTime = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60)
      .toString()
      .padStart(2, "0");
    const secs = (totalSeconds % 60).toString().padStart(2, "0");
    return `${minutes}:${secs}`;
  };

  useEffect(() => {
    if (!isTokenFetched) {
      generateToken();
    }
  }, [isTokenFetched]);

  const { join, leave, isJoined } = useJoin(
    { appid: appId, channel: channel, token: token, uid: null },
    shouldJoin
  );

  // Function to upload files and get URLs
  const uploadFiles = async () => {
    const uploadPromises = [];
    const fileUrls = {};

    // Upload assessment files
    if (assessmentFiles.length > 0) {
      assessmentFiles.forEach((file) => {
        uploadPromises.push(
          dispatch(uploadFileApi(file)).then((result) => {
            if (result.payload.status === "success") {
              fileUrls.assessmentsFileURL = result.payload.url;
              // Use the assessment input field value as filename, fallback to actual filename if empty
              fileUrls.assessmentsFileName = assessments || file.name;
            }
          })
        );
      });
    }

    // Upload exercise files
    if (exerciseFiles.length > 0) {
      exerciseFiles.forEach((file) => {
        uploadPromises.push(
          dispatch(uploadFileApi(file)).then((result) => {
            if (result.payload.status === "success") {
              fileUrls.exercisesFileURL = result.payload.url;
              // Use the exercises input field value as filename, fallback to actual filename if empty
              fileUrls.exercisesFileName = exercises || file.name;
            }
          })
        );
      });
    }

    // Upload recommendation files
    if (recommendationFiles.length > 0) {
      recommendationFiles.forEach((file) => {
        uploadPromises.push(
          dispatch(uploadFileApi(file)).then((result) => {
            if (result.payload.status === "success") {
              fileUrls.recommendationsFileURL = result.payload.url;
              fileUrls.recommendationsFileName = file.name;
            }
          })
        );
      });
    }

    await Promise.all(uploadPromises);
    return fileUrls;
  };

  const submitCall = async () => {
    // Validate risk status is selected - do NOT use fallback
    if (!value) {
      message.error("Please rate the student profile before submitting.");
      return;
    }

    const selectedValue = value;
    setSubmitLoading(true);

    try {
      // Step 1: Upload files first
      const fileUrls = await uploadFiles();

      // Step 2: Prepare consultation observation data
      const observationData = {
        appointmentId: id,
        selfHarmSymptoms: selfHarmSymptoms,
        depressionSymptoms: depressionSymptoms,
        anxietySymptoms: anxietySymptoms,
        angerIssues: angerIssues,
        clinicalConsultation: clinicalConsultation,
        medicalConsultation: medicalConsultation,
        notesText: notes || "",
        recommendationText: prescription || "",
        noPrescriptionRequired: prescriptionNotRequired,
        ...fileUrls // Spread the uploaded file URLs
      };

      // Step 3: Send consultation observation data
      const observationResult = await dispatch(consultationObservationApi(observationData));

      if (observationResult.payload.status === "success") {
        // Step 4: Update appointment status using the existing API
        let userData;
        
        // Handle different scenarios for appointment status
        if (prescriptionNotRequired) {
          // If prescription not required is marked true, status is COMPLETED
          userData = {
            notes: notes,
            prescriptionRequired: false,
            id: id,
            status: "COMPLETED",
            riskStatus: selectedValue,
            studentJoined: true,
          };
        } else {
          // Check if recommendations are provided (any of: prescription text/files, assessments text/files,
          // exercises text/files, clinical consultation, or medical consultation)
          const hasRecommendationText = prescription && prescription.trim() !== "";
          const hasRecommendationFiles = recommendationFiles && recommendationFiles.length > 0;
          const hasAssessmentText = assessments && assessments.trim() !== "";
          const hasAssessmentFiles = assessmentFiles && assessmentFiles.length > 0;
          const hasExerciseText = exercises && exercises.trim() !== "";
          const hasExerciseFiles = exerciseFiles && exerciseFiles.length > 0;
          const hasClinicalConsultation = clinicalConsultation === true;
          const hasMedicalConsultation = medicalConsultation === true;

          const hasRecommendations = hasRecommendationText || hasRecommendationFiles ||
                                     hasAssessmentText || hasAssessmentFiles ||
                                     hasExerciseText || hasExerciseFiles ||
                                     hasClinicalConsultation || hasMedicalConsultation;

          if (forceEndWithoutPrescription || !hasRecommendations) {
            // No recommendations provided - status should be IN_COMPLETE
            userData = {
              notes: notes,
              prescription: prescription || "",
              id: id,
              status: "IN_COMPLETE",
              riskStatus: selectedValue,
              studentJoined: true,
            };
          } else {
            // Recommendations provided - status should be COMPLETED
            userData = {
              notes: notes,
              prescription: prescription,
              id: id,
              status: "COMPLETED",
              riskStatus: selectedValue,
              studentJoined: true,
            };
          }
        }
        
        // Step 5: Update appointment status
        const appointmentResult = await dispatch(endCallApi(userData));
        
        if (appointmentResult.payload.status === "success") {
          const statusMessage = userData.status === "COMPLETED" 
            ? "Appointment Completed Successfully" 
            : "Appointment Saved as Incomplete";
          
          message.success(statusMessage).then(() => {
            setSubmitLoading(false);
            // Clear uploaded files from Redux state
            dispatch(clearUploadedFiles());
            try {
              localStorage.removeItem(draftKey);
            } catch (e) {
              console.error("Failed to clear call draft", e);
            }
            navigateTo("/upcoming-appointments/upcoming");
          });
        } else {
          setSubmitLoading(false);
          message.error("Failed to update appointment status. Please try again.");
        }
      } else {
        setSubmitLoading(false);
        message.error("Failed to save consultation observations. Please try again.");
      }
    } catch (error) {
      setSubmitLoading(false);
      message.error("An error occurred. Please try again.");
    }
  };

  useEffect(() => {
    if (token) {
      setShouldJoin(true);
    }
  }, [token]);

  const { localMicrophoneTrack } = useLocalMicrophoneTrack(true); // always true
  useEffect(() => {
    if (localMicrophoneTrack) {
      localMicrophoneTrack.setEnabled(micOn);
    }
  }, [micOn, localMicrophoneTrack]);
  const { localCameraTrack } = useLocalCameraTrack(cameraOn);
  usePublish([
    micOn ? localMicrophoneTrack : null,
    cameraOn ? localCameraTrack : null,
  ]);

  const client = useRTCClient();
  const remoteUsers = useRemoteUsers();

  const endCall = () => {
    const payload = {};
    dispatch(getAppointment(payload)).then((res) => {
      let selectedData = Object.values(res?.payload || {}).flat();
      let filterData = selectedData.filter((item) => item.appointmentId === id);

      if (Array.isArray(filterData) && filterData.length > 0 && filterData[0]) {
        if (
          filterData[0].studentJoined === null ||
          !filterData[0].studentJoined
        ) {
          setGoBackModel(true);
        } else {
          // Show warning only if prescription is not marked as "not required"
          // AND user hasn't provided any recommendations (text/files/assessments/exercises/consultations)
          const hasRecommendationContent =
            (prescription !== null && prescription.trim() !== "") ||
            (recommendationFiles && recommendationFiles.length > 0) ||
            (assessments && assessments.trim() !== "") ||
            (assessmentFiles && assessmentFiles.length > 0) ||
            (exercises && exercises.trim() !== "") ||
            (exerciseFiles && exerciseFiles.length > 0) ||
            clinicalConsultation === true ||
            medicalConsultation === true;

          if (forceEndWithoutPrescription || prescriptionNotRequired || hasRecommendationContent) {
            setIsModalOpen(true);
          } else {
            setPrescriptionAlert(true);
          }
        }
      }
    });
  };

  const handleContinueWithoutPrescription = () => {
    setForceEndWithoutPrescription(true);
    setPrescriptionAlert(false);
    setIsModalOpen(true); 
  };

  const handleRiskCancel = () => {
    setIsModalOpen(false);
    setForceEndWithoutPrescription(false); 
  };

  useEffect(() => {
    if (!client) return;

    const handleUserLeft = (user) => {
      setLeftCall(true);
    };

    client.on("user-left", handleUserLeft);

    return () => {
      client.off("user-left", handleUserLeft);
    };
  }, [client]);

  // Renew the token before it expires, and offer a rejoin if the connection drops.
  useEffect(() => {
    if (!client) return;

    const handleTokenWillExpire = async () => {
      try {
        const freshToken = await fetchFreshToken();
        if (freshToken) {
          await client.renewToken(freshToken);
        }
      } catch (e) {
        console.error("Failed to renew call token", e);
      }
    };

    const handleConnectionStateChange = (curState, _prevState, reason) => {
      if (curState === "DISCONNECTED" && reason !== "LEAVE") {
        setConnectionLost(true);
      } else if (curState === "CONNECTED") {
        setConnectionLost(false);
      }
    };

    client.on("token-privilege-will-expire", handleTokenWillExpire);
    client.on("connection-state-change", handleConnectionStateChange);

    return () => {
      client.off("token-privilege-will-expire", handleTokenWillExpire);
      client.off("connection-state-change", handleConnectionStateChange);
    };
  }, [client, id]);

  const rejoinCall = async () => {
    setRejoining(true);
    try {
      const freshToken = await fetchFreshToken();
      if (!freshToken) {
        throw new Error("No token returned");
      }
      await client.join(appId, channel, freshToken, null);
      // usePublish won't republish tracks it already sent before the drop.
      const tracks = [micOn && localMicrophoneTrack, cameraOn && localCameraTrack].filter(Boolean);
      if (tracks.length > 0) {
        await client.publish(tracks);
      }
      setConnectionLost(false);
    } catch (e) {
      console.error("Failed to rejoin call", e);
      message.error("Could not rejoin the call. Please try again.");
    } finally {
      setRejoining(false);
    }
  };

  useEffect(() => {
    const connection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;

    const updateSignal = () => {
      if (connection) {
        const downlink = connection.downlink;
        let level = 0;

        // Simple rule-based approximation
        if (downlink > 10) level = 4;
        else if (downlink > 5) level = 3;
        else if (downlink > 2) level = 2;
        else if (downlink > 0) level = 1;
        else level = 0;

        setSignalLevel(level);
      }
    };

    if (connection) {
      updateSignal();
      connection.addEventListener("change", updateSignal);

      return () => {
        connection.removeEventListener("change", updateSignal);
      };
    }
  }, []);

  useEffect(() => {
    if (remoteUsers.length > 0) {
      // check if any remote user is muted
      const isMuted = remoteUsers.some((user) => user._audio_muted_);
      setRemoteMuted(isMuted);
    }
  }, [remoteUsers]);

  return (
    <Container fluid className="video-main-container">
      <Container fluid className="video-inner">
        <Row>
          <Col md={8} style={{ marginTop: "100px", position: "relative" }}>
            {connectionLost && (
              <div
                className="flex flex-wrap items-center justify-between gap-3 rounded-xl p-4 mb-3 font-poppins"
                style={{ background: "#FFF4E5", border: "1px solid #FFAF77" }}
              >
                <span className="text-[16px] text-[#171717]">
                  Connection lost. Rejoin to continue the session, or end it to save the risk rating and notes.
                </span>
                <div className="flex gap-2">
                  <Button type="primary" loading={rejoining} onClick={rejoinCall}>
                    Rejoin
                  </Button>
                  <Button danger disabled={rejoining} onClick={endCall}>
                    End Session
                  </Button>
                </div>
              </div>
            )}
            <div className="room">
              {isConnected ? (
                <div className="user-list">
                  {/* Remote Users */}
                  <>
                    {remoteUsers.length > 0 ? (
                      <>
                        {remoteUsers.map((user) => {
                          return (
                            <div
                              key={user.uid}
                              style={{
                                width: "100%",
                                height: "80vh",
                                borderRadius: "20px",
                                margin: "10px",
                              }}
                            >
                              <RemoteUser
                                cover="https://expert.phybercare.com/favicon.png"
                                user={user}
                                cameraOn={cameraOn}
                                style={{ borderRadius: "20px" }}
                              >
                                <samp className="user-name">{user.uid}</samp>
                              </RemoteUser>
                            </div>
                          );
                        })}
                      </>
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: "80vh",
                          borderRadius: "20px",
                          margin: "10px",
                          background: "rgba(0,0,0,0.5)",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <p className="font-poppins text-[18px] text-[#FFFFFF]">
                          Waiting for user to join...
                        </p>
                      </div>
                    )}
                  </>

                  {/* Local User floating at bottom right */}
                  <div
                    className="video-overlay-hidden"
                    style={{
                      width: "229px",
                      height: "148px",
                      position: "fixed",
                      bottom: "10vh",
                      right: "35%",
                      zIndex: "9999",
                      borderRadius: "20px",
                      overflow: "hidden",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                    }}
                  >
                    <LocalUser
                      videoTrack={localCameraTrack}
                      cameraOn={cameraOn}
                      micOn={micOn}
                      style={{
                        borderRadius: "20px",
                        width: "100%",
                        height: "100%",
                      }}
                      cover="https://expert.phybercare.com/favicon.png"
                    >
                      <samp className="user-name">You</samp>
                    </LocalUser>
                  </div>
                  <div
                    style={{
                      width: "25%",
                      height: "200px",
                      position: "fixed",
                      top: "15%",
                      left: "40px",

                      zIndex: "9999",
                    }}
                  >
                    <div
                      style={{
                        width: "150px",
                        height: "30px",
                        background: "black",
                        display: "flex",
                        gap: "8px",
                        borderRadius: "8px",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: "4px",
                          alignItems: "flex-end",
                          height: "20px",
                          justifyContent: "center",
                        }}
                      >
                        {[1, 2, 3, 4].map((bar) => (
                          <div
                            key={bar}
                            style={{
                              width: "4px",
                              height: `${bar * 5}px`,
                              backgroundColor:
                                bar <= signalLevel ? "#4CAF50" : "#ccc",
                              borderRadius: "2px",
                              transition: "background-color 0.3s",
                            }}
                          />
                        ))}
                      </div>
                      <span
                        style={{
                          fontFamily: "poppins",
                          fontWeight: 600,
                          color: "#FFFFFF",
                        }}
                      >
                        ⏱️ {formatTime(seconds)}
                      </span>
                      <>
                        {remoteUsers.length > 0 && (
                          <>
                            {remoteMuted ? (
                              <FiMicOff color="#FFFFFF" />
                            ) : (
                              <LuMic color="#FFFFFF" />
                            )}
                          </>
                        )}
                      </>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            {/* Controls */}
            {isConnected && (
              <div
                style={{
                  width: "100%",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <button
                    className="btn"
                    onClick={() => {
                      if (mode === "VIDEO") {
                        setCameraOn((prev) => !prev);
                      }
                    }}
                    
                  >
                    <i className={`i-camera ${cameraOn ? "" : "off"}`} />
                  </button>
                  <button
                    className="btn"
                    onClick={() => setMicOn((prev) => !prev)}
                  >
                    <i className={`i-microphone ${micOn ? "" : "off"}`} />
                  </button>
                  <button
                    className={`btn btn-phone ${
                      calling ? "btn-phone-active" : ""
                    }`}
                    onClick={() => {
                      endCall();
                    }}
                  >
                    {calling ? (
                      <i className="i-phone-hangup" />
                    ) : (
                      <i className="i-mdi-phone" />
                    )}
                  </button>
                </div>
              </div>
            )}
          </Col>

          {/* RIGHT SIDE */}
          <Col md={4} style={{ marginTop: "120px" }}>
            {/* Refreshes every 30s since the client can share during the call */}
            <SharedDocuments
              appointmentId={id}
              pollInterval={30000}
              className="mb-3 max-h-[35vh] overflow-y-auto"
            />
            <NotesAndRecommendations
              notes={notes}
              setNotes={setNotes}
              prescription={prescription}
              setPrescription={setPrescription}
              prescriptionNotRequired={prescriptionNotRequired}
              inCall={true}
              setPrescriptionNotRequired={setPrescriptionNotRequired}
              recommendationFiles={recommendationFiles}
              setRecommendationFiles={setRecommendationFiles}
              // New observation props
              selfHarmSymptoms={selfHarmSymptoms}
              setSelfHarmSymptoms={setSelfHarmSymptoms}
              depressionSymptoms={depressionSymptoms}
              setDepressionSymptoms={setDepressionSymptoms}
              anxietySymptoms={anxietySymptoms}
              setAnxietySymptoms={setAnxietySymptoms}
              angerIssues={angerIssues}
              setAngerIssues={setAngerIssues}
              // New recommendation props
              assessments={assessments}
              setAssessments={setAssessments}
              exercises={exercises}
              setExercises={setExercises}
              clinicalConsultation={clinicalConsultation}
              setClinicalConsultation={setClinicalConsultation}
              medicalConsultation={medicalConsultation}
              setMedicalConsultation={setMedicalConsultation}
              // File upload props
              assessmentFiles={assessmentFiles}
              setAssessmentFiles={setAssessmentFiles}
              exerciseFiles={exerciseFiles}
              setExerciseFiles={setExerciseFiles}
            />
          </Col>
        </Row>
      </Container>

      {/* Modals */}
      <RiskRatingModal
        isOpen={isModalOpen}
        onCancel={handleRiskCancel}
        onSubmit={submitCall}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        submitLoading={submitLoading}
        getRiskColor={getRiskColor}
      />

      <Modal open={leftCall} onOk={() => setLeftCall(false)} closable={false}>
        <h3 className="risk-title">Student left call.</h3>
      </Modal>

      <PrescriptionWarningModal
        isOpen={prescriptionAlert}
        onClose={() => setPrescriptionAlert(false)}
        onContinueWithoutPrescription={handleContinueWithoutPrescription}
      />
      {goBackModel === true ? (
        <GoBackModel
          goBackModel={goBackModel}
          setGoBackModel={setGoBackModel}
        />
      ) : null}

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </Container>
  );
}
